import { useParams } from 'react-router-dom'
import {
  AlertMessage,
  ConfigurableText,
  NavigationPage,
} from '@/app/components'
import { useAppSelector } from '@/hooks'
import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'
import Collapsible from '@/components/collapsibleList'
import { capitalize, capitalizeFirstLetter } from '@/utils/stringUtilities'
import { CollapsibleListItemHeader } from '../components/CollapsibleListItemHeader'
import { Children, useState } from 'react'
import { VehicleLabel } from '@/app/components/TeslaVehiclePermissions/TeslaVehiclePermissions'
import { canCharge } from '@/utils/vehicles/permissions'
import { useNavigation } from '@/app/hooks'
import { Vehicle } from '@/types'
import PageLoader from '@/authenticated/components/layout/PageLoader'
import useNavigateToTeslaKeyPairing from '@/app/hooks/useNavigateToTeslaKeyPairing'
import useRefreshPermissionsIfNeeded from './useRefreshPermissionsIfNeeded'
import { logEvent } from '@/logging'
import { getPermissionsEditUrl } from '@/app/pages/tesla-authentication/verify-permissions/utils'
import { Tag } from '@/components'
import {
  requiredPermissions,
  suggestedPermissions,
} from '@/app/pages/tesla-authentication/utils/permissions'
import getFleetMigrationBanner from '@/app/components/DeviceSelectionGallery/banners/vehicle/FleetMigrationBanner'

const SuggestedBadge = () => (
  <Tag color="blue-100">
    <ConfigurableText
      variant="body5"
      className="text-xs font-bold text-blue-500"
    >
      Suggested
    </ConfigurableText>
  </Tag>
)

const RequiredBadge = () => (
  <Tag color="purple-100">
    <ConfigurableText
      variant="body5"
      className="text-xs font-bold text-purple-500"
    >
      Required
    </ConfigurableText>
  </Tag>
)

const List = (props: { title: string; children: React.ReactNode }) => {
  return (
    <div className="flex flex-col gap-2 mb-2 only:mb-0">
      <ConfigurableText variant="body4">{props.title}</ConfigurableText>
      <ul className="w-full pl-6 list-disc list-outside">
        {Children.map(props.children, (child) => (
          <li className="w-full">{child}</li>
        ))}
      </ul>
    </div>
  )
}

function CapabilityLoading() {
  return <PageLoader />
}

function CapabilityLoaded(props: {
  vehicle: Vehicle
  errorRefreshingPermissions: boolean
}) {
  const navigate = useNavigation()
  const permissionsEditUrl = getPermissionsEditUrl()
  const navigateToEditUrl = () => {
    logEvent('capabilities_opened_permissions')
    window.open(permissionsEditUrl, '_blank', 'noopener,noreferrer')
  }
  const { isLoading, navigate: navigateToTeslaKeyPairing } =
    useNavigateToTeslaKeyPairing()

  const isTesla = props.vehicle.car.car_model?.make.toLowerCase() === 'tesla'
  const canControlCharge = canCharge(props.vehicle)
  const missingScopes = props.vehicle.car.fleet_scopes?.missing ?? []
  const uncontrollableTesla =
    isTesla &&
    props.vehicle.requires_tesla_fleet_migration &&
    props.vehicle.car?.tesla_command_protocol_disabled

  const configStatus = {
    /**=================================**/
    // Tesla capabilities
    'Profile Information':
      !uncontrollableTesla && !missingScopes.includes('user_data'),
    'Vehicle Information':
      !uncontrollableTesla && !missingScopes.includes('vehicle_device_data'),
    'Vehicle Location':
      !uncontrollableTesla && !missingScopes.includes('vehicle_location'),
    'Vehicle Commands':
      !uncontrollableTesla && !missingScopes.includes('vehicle_cmds'),
    'Vehicle Charging Management':
      !uncontrollableTesla && !missingScopes.includes('vehicle_charging_cmds'),
    'Energy Product Information':
      !uncontrollableTesla && !missingScopes.includes('energy_device_data'),
    'Energy Product Settings':
      !uncontrollableTesla && !missingScopes.includes('energy_cmds'),
    'Virtual Key':
      !uncontrollableTesla && !props.vehicle.car.public_key_not_attached_at,
    /**=================================**/
    // Non-Tesla Capabilities
    'Charge Control': !uncontrollableTesla && canControlCharge,
    'Vehicle Data': !uncontrollableTesla,
    'Charge Data': !uncontrollableTesla,
  }

  const initialOpenState: Record<string, boolean> = {}
  Object.keys(configStatus).forEach((permission) => {
    initialOpenState[permission] =
      !configStatus[permission as keyof typeof configStatus]
  })

  // Track which items are open
  const [openItems, setOpenItems] =
    useState<Record<string, boolean>>(initialOpenState)

  const toggleItem = (permission: string, open: boolean) => {
    setOpenItems((prev) => ({
      ...prev,
      [permission]: open,
    }))
  }

  const capabilityConfig: {
    visible: boolean
    title: keyof typeof configStatus
    inError: boolean
    isCorePermission: boolean
    content: React.ReactNode
    accessLevel?: 'required' | 'suggested' | 'optional'
    extraContent?: React.ReactNode
  }[] = [
    {
      visible: isTesla && !props.vehicle.requires_tesla_fleet_migration,
      title: 'Virtual Key',
      inError: !configStatus['Virtual Key'],
      isCorePermission: false,
      accessLevel: 'required',
      content: configStatus['Virtual Key'] ? (
        <ConfigurableText variant="body4">
          Required for Charge Settings to work. Tesla will not let Optiwatt
          control your car without this.
        </ConfigurableText>
      ) : (
        <>
          <ConfigurableText variant="body4">
            Required for Charge Settings to work. Tesla will not let Optiwatt
            control your car without this.
          </ConfigurableText>
          <ConfigurableText
            id="PairVirtualKey"
            variant="link"
            onClick={() => navigateToTeslaKeyPairing(props.vehicle)}
          >
            Click to pair your virtual key
          </ConfigurableText>
        </>
      ),
    },
    {
      visible: isTesla,
      title: 'Profile Information',
      inError: !configStatus['Profile Information'],
      isCorePermission: false,
      content: configStatus['Profile Information'] ? (
        <List title="You have access to features including:">
          <ConfigurableText variant="body4">
            Personalized communication
          </ConfigurableText>
          <ConfigurableText variant="body4">Offers</ConfigurableText>
        </List>
      ) : (
        <>
          <List title="Required to access features including:">
            <ConfigurableText variant="body4">
              Personalized communication
            </ConfigurableText>
            <ConfigurableText variant="body4">Offers</ConfigurableText>
          </List>
          <ConfigurableText
            id="UpgradeChargingManagement"
            variant="link"
            onClick={navigateToEditUrl}
          >
            Click to upgrade permissions
          </ConfigurableText>
        </>
      ),
    },
    {
      visible: isTesla,
      title: 'Vehicle Information',
      inError: !configStatus['Vehicle Information'],
      accessLevel: 'required',
      isCorePermission: true,
      content: configStatus['Vehicle Information'] ? (
        <List title="You have access to features including:">
          <ConfigurableText variant="body4">
            Save Money or Save The Planet
          </ConfigurableText>
          <ConfigurableText variant="body4">
            Scheduled Departure
          </ConfigurableText>
          <ConfigurableText variant="body4">Trips</ConfigurableText>
        </List>
      ) : (
        <>
          <ConfigurableText variant="body4">
            The vehicle information permission is necessary to continue.
          </ConfigurableText>
          <ConfigurableText
            id="UpdateVehiclePermissions"
            variant="link"
            onClick={navigateToEditUrl}
          >
            Click to upgrade permissions
          </ConfigurableText>
        </>
      ),
    },
    {
      visible: isTesla,
      title: 'Vehicle Location',
      inError: !configStatus['Vehicle Location'],
      isCorePermission: true,
      content: (
        <>
          <List
            title={
              configStatus['Vehicle Location']
                ? 'You have access to features including:'
                : 'Required to access features including:'
            }
          >
            <ConfigurableText variant="body4">Manage Charging</ConfigurableText>
            <ConfigurableText variant="body4">
              Save Money or Save The Planet
            </ConfigurableText>
            <ConfigurableText variant="body4">
              Scheduled Departure
            </ConfigurableText>
            <ConfigurableText variant="body4">Trips</ConfigurableText>
          </List>
          {!configStatus['Vehicle Location'] && (
            <ConfigurableText
              id="UpgradeVehicleLocation"
              variant="link"
              onClick={navigateToEditUrl}
            >
              Click to upgrade permissions
            </ConfigurableText>
          )}
        </>
      ),
    },
    {
      visible: isTesla,
      title: 'Vehicle Charging Management',
      inError: !configStatus['Vehicle Charging Management'],
      accessLevel: 'required',
      isCorePermission: false,
      content: configStatus['Vehicle Charging Management'] ? (
        <List title="You have access to features including:">
          <ConfigurableText variant="body4">Manage Charging</ConfigurableText>
          <ConfigurableText variant="body4">
            Save Money or Save The Planet
          </ConfigurableText>
          <ConfigurableText variant="body4">
            Scheduled Departure
          </ConfigurableText>
          <ConfigurableText variant="body4">Trips</ConfigurableText>
        </List>
      ) : (
        <>
          <List title="Required to access features including:">
            <ConfigurableText variant="body4">Manage Charging</ConfigurableText>
            <ConfigurableText variant="body4">
              Save Money or Save The Planet
            </ConfigurableText>
            <ConfigurableText variant="body4">
              Scheduled Departure
            </ConfigurableText>
            <ConfigurableText variant="body4">Trips</ConfigurableText>
          </List>
          <ConfigurableText
            id="UpgradeChargingManagement"
            variant="link"
            onClick={navigateToEditUrl}
          >
            Click to upgrade permissions
          </ConfigurableText>
        </>
      ),
    },
    {
      visible: isTesla,
      title: 'Vehicle Commands',
      inError: !configStatus['Vehicle Commands'],
      isCorePermission: false,
      content: configStatus['Vehicle Commands'] ? (
        <List title="You have access to features including:">
          <ConfigurableText variant="body4">
            Climate Preconditioning
          </ConfigurableText>
        </List>
      ) : (
        <>
          <ConfigurableText variant="body4">
            The vehicle commands permission enables the Climate Preconditioning
            feature.
          </ConfigurableText>
          <ConfigurableText
            id="UpdateVehiclePermissions"
            variant="link"
            onClick={navigateToEditUrl}
          >
            Click to upgrade permissions
          </ConfigurableText>
        </>
      ),
    },
    {
      visible: isTesla,
      title: 'Energy Product Information',
      inError: !configStatus['Energy Product Information'],
      isCorePermission: false,
      content: configStatus['Energy Product Information'] ? (
        <List title="You have access to features including:">
          <ConfigurableText variant="body4">
            Home Energy Insights
          </ConfigurableText>
          <ConfigurableText variant="body4">
            Home Energy Management
          </ConfigurableText>
          <ConfigurableText variant="body4">
            Import Costs or Export Revenue
          </ConfigurableText>
          <ConfigurableText variant="body4">
            Toggle Operation Mode
          </ConfigurableText>
        </List>
      ) : (
        <>
          <List title="Required to support future features including:">
            <ConfigurableText variant="body4">
              Home Energy Insights
            </ConfigurableText>
            <ConfigurableText variant="body4">
              Home Energy Management
            </ConfigurableText>
            <ConfigurableText variant="body4">
              Import Costs or Export Revenue
            </ConfigurableText>
            <ConfigurableText variant="body4">
              Toggle Operation Mode
            </ConfigurableText>
          </List>
          <ConfigurableText
            id="UpgradeEnergyProductInformation"
            variant="link"
            onClick={navigateToEditUrl}
          >
            Click to upgrade permissions
          </ConfigurableText>
        </>
      ),
    },
    {
      visible: isTesla,
      title: 'Energy Product Settings',
      inError: !configStatus['Energy Product Settings'],
      isCorePermission: false,
      content: configStatus['Energy Product Settings'] ? (
        <List title="Your access supports future features including:">
          <ConfigurableText variant="body4">
            Home Energy Insights
          </ConfigurableText>
          <ConfigurableText variant="body4">
            Home Energy Management
          </ConfigurableText>
          <ConfigurableText variant="body4">
            Import Costs or Export Revenue
          </ConfigurableText>
          <ConfigurableText variant="body4">
            Toggle Operation Mode
          </ConfigurableText>
        </List>
      ) : (
        <>
          <List title="Required to support future features including:">
            <ConfigurableText variant="body4">
              Home Energy Insights
            </ConfigurableText>
            <ConfigurableText variant="body4">
              Home Energy Management
            </ConfigurableText>
            <ConfigurableText variant="body4">
              Import Costs or Export Revenue
            </ConfigurableText>
            <ConfigurableText variant="body4">
              Toggle Operation Mode
            </ConfigurableText>
          </List>
          <ConfigurableText
            id="UpgradeEnergyProductSettings"
            variant="link"
            onClick={navigateToEditUrl}
          >
            Click to upgrade permissions
          </ConfigurableText>
        </>
      ),
    },
    {
      visible: !isTesla,
      title: 'Charge Control',
      inError: !configStatus['Charge Control'],
      isCorePermission: false,
      content: configStatus['Charge Control'] ? (
        <List title="This vehicle supports:">
          <ConfigurableText variant="body4">
            Save Money or Save The Planet
          </ConfigurableText>
          <ConfigurableText variant="body4">
            Scheduled Departure
          </ConfigurableText>
          <ConfigurableText variant="body4">Trips</ConfigurableText>
        </List>
      ) : (
        <List title="This vehicle does not support:">
          <ConfigurableText variant="body4">
            Save Money or Save The Planet
          </ConfigurableText>
          <ConfigurableText variant="body4">
            Scheduled Departure
          </ConfigurableText>
          <ConfigurableText variant="body4">Trips</ConfigurableText>
        </List>
      ),
    },
    {
      visible: !isTesla,
      title: 'Vehicle Data',
      inError: !configStatus['Vehicle Data'],
      isCorePermission: false,
      content: configStatus['Vehicle Data'] ? (
        <List title="This vehicle supports:">
          <ConfigurableText variant="body4">
            Location information
          </ConfigurableText>
          <ConfigurableText variant="body4">Gasoline savings</ConfigurableText>
        </List>
      ) : (
        <List title="This vehicle does not support:">
          <ConfigurableText variant="body4">
            Location information
          </ConfigurableText>
          <ConfigurableText variant="body4">Gasoline savings</ConfigurableText>
        </List>
      ),
    },
    {
      visible: !isTesla,
      title: 'Charge Data',
      inError: configStatus['Vehicle Data'],
      isCorePermission: false,
      content: configStatus['Charge Data'] ? (
        <List title="This vehicle supports:">
          <ConfigurableText variant="body4">Program rewards</ConfigurableText>
          <ConfigurableText variant="body4">Charging insights</ConfigurableText>
        </List>
      ) : (
        <List title="This vehicle does not support:">
          <ConfigurableText variant="body4">Program rewards</ConfigurableText>
          <ConfigurableText variant="body4">Charging insights</ConfigurableText>
        </List>
      ),
    },
  ]

  const make = capitalize(props.vehicle.car.car_model?.make || '')

  const capabilityToDisplay = capabilityConfig.filter(
    (config) => config.visible,
  )
  const sortedCapabilityToDisplay = [...capabilityToDisplay].sort((a, b) => {
    // First sort by inError
    if (a.inError !== b.inError) {
      return a.inError ? -1 : 1
    }
    // If both have same error status, sort by isCorePermission
    if (a.isCorePermission !== b.isCorePermission) {
      return a.isCorePermission ? -1 : 1
    }
    // If both have same error and core permission status, maintain original order
    return 0
  })

  const showAlert = missingScopes.length > 0
  const iconType = () => {
    const missingRequiredPermissions = capabilityToDisplay.some(
      (config) => config.accessLevel === 'required' && config.inError,
    )

    if (isTesla && props.vehicle.requires_tesla_fleet_migration) {
      if (props.vehicle.car?.tesla_command_protocol_disabled) {
        return 'noPermissions'
      }
      return 'missingPermissions'
    }

    if (!missingRequiredPermissions) {
      return 'connected'
    }

    return 'missingPermissions'
  }

  if (isLoading) {
    return <PageLoader />
  }

  return (
    <NavigationPage
      id="capabilities_page"
      title="Capabilities"
      subtitle={`Manage your permissions with ${make}`}
    >
      <AlertMessage
        variant="error"
        show={props.errorRefreshingPermissions}
        className="mb-2"
      >
        Your permissions in Optiwatt might be out of sync with what you have
        enabled. We'll automatically sync them later.
      </AlertMessage>
      {isTesla && props.vehicle.requires_tesla_fleet_migration && (
        <div className="mb-4">
          {getFleetMigrationBanner({ vehicle: props.vehicle }).renderBanner()}
        </div>
      )}
      <Collapsible.List className="!p-0">
        <Collapsible.ListItem
          className="[&>:first-child]:px-4"
          isOpen={true}
          header={
            <ConfigurableText variant="body4">
              <VehicleLabel
                name={
                  props.vehicle.car.display_name !== ''
                    ? props.vehicle.car.display_name
                    : capitalizeFirstLetter(
                        props.vehicle.car.car_model?.make ?? '',
                      )
                }
                model={props.vehicle.car.car_model?.friendly_name ?? ''}
                iconType={iconType()}
              />
            </ConfigurableText>
          }
        >
          <Collapsible.List className="border-0 border-t-[1px] rounded-t-none">
            {sortedCapabilityToDisplay
              .filter((c) => c.visible)
              .map((c) => {
                const isOpen = openItems[c.title] ?? !configStatus[c.title]
                const isSuggested =
                  c.accessLevel === 'suggested' ||
                  suggestedPermissions.includes(c.title as any)
                const isRequired =
                  c.accessLevel === 'required' ||
                  requiredPermissions.includes(c.title as any)

                let badgeElement = undefined
                if (isRequired && c.inError) {
                  badgeElement = <RequiredBadge />
                } else if (isSuggested && c.inError) {
                  badgeElement = <SuggestedBadge />
                }

                let status = configStatus[c.title as keyof typeof configStatus]
                  ? 'success'
                  : 'failure'

                // Tesla overrides if we need to update to fleet API
                if (isTesla && props.vehicle.requires_tesla_fleet_migration) {
                  // If tesla command protocol is disabled, this is a bigger problem
                  // and the user should know we can't do anything with their car
                  status = props.vehicle.car?.tesla_command_protocol_disabled
                    ? 'failure'
                    : 'warning'
                }

                return (
                  <Collapsible.ListItem
                    key={c.title}
                    isOpen={isOpen}
                    header={
                      <CollapsibleListItemHeader
                        title={c.title}
                        status={
                          status as
                            | 'success'
                            | 'warning'
                            | 'failure'
                            | 'unknown'
                        }
                        rightElement={
                          (isRequired || isSuggested) && !isOpen
                            ? badgeElement
                            : undefined
                        }
                      />
                    }
                    onToggle={(open) => toggleItem(c.title, open)}
                  >
                    {(isRequired || isSuggested) && isOpen && (
                      <div className="mb-3 flex">{badgeElement}</div>
                    )}
                    {c.content}
                    {c.extraContent ?? null}
                  </Collapsible.ListItem>
                )
              })}
          </Collapsible.List>
        </Collapsible.ListItem>
      </Collapsible.List>
      <AlertMessage show={showAlert} variant="info" className="mt-4">
        {`Don't want to enable vehicle permissions? Connect your charger instead. `}
        <ConfigurableText
          id="ConnectChargerButton"
          variant="link"
          onClick={() => navigate.push('/connect-device/charger/select')}
        >
          {`Connect Smart Charger`}
        </ConfigurableText>
      </AlertMessage>
    </NavigationPage>
  )
}

export default function CapabilityPage() {
  type PathParams = {
    localGridId: string
    deviceType: DeviceType | undefined
    deviceId: string | undefined
  }
  const { deviceId } = useParams<PathParams>()
  const vehiclesLoading = useAppSelector((state) => state.vehicles.isLoading)
  const selectedVehicle = useAppSelector((state) => {
    if (!deviceId) return null
    return state.vehicles.vehicles?.find(
      (vehicle) => vehicle.id === parseInt(deviceId),
    )
  })
  const {
    isLoading: isLoadingPermissionRefresh,
    error: permissionRefreshError,
  } = useRefreshPermissionsIfNeeded(selectedVehicle?.id)

  if (!selectedVehicle || vehiclesLoading || isLoadingPermissionRefresh) {
    return <CapabilityLoading />
  }

  return (
    <CapabilityLoaded
      vehicle={selectedVehicle}
      errorRefreshingPermissions={permissionRefreshError}
    />
  )
}
